import _ from "lodash";
import React, { useState } from "react";

// FileUploader component
const FileUploader = React.forwardRef(
    ({
        id,
        name,
        label,
        value = null,
        required = false,
        multiple = false,
        maxFiles = 1,
        isHideLabel = false,
        className = "",
        subTitle = "",
        subRequired = false,
        disabled = false,
        accept = "",
        onChange = null,
        errors = null,
        errorName = "",
        sizeInMb = 1,
        uploadedImage = null, // <-- remote image URL, separate
    }, ref) => {

        const [error, setError] = useState("");
        const [uploadedFiles, setUploadedFiles] = useState([]); // <-- only local files

        const msg = _.get(errors || {}, errorName || name);

        // PDF Icon SVG Component
        const PdfIcon = () => (
            <svg className="w-16 h-16" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="8" y="4" width="40" height="56" rx="2" fill="#E53E3E" />
                <path d="M48 14L38 4V12C38 13.1046 38.8954 14 40 14H48Z" fill="#ffffff" />
                <text x="30" y="38" fontSize="12" fill="white" fontWeight="bold" textAnchor="middle">PDF</text>
            </svg>
        );

        // Generic File Icon SVG Component
        const FileIcon = () => (
            <svg className="w-16 h-16" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="8" y="4" width="40" height="56" rx="2" fill="#718096" />
                <path d="M48 14L38 4V12C38 13.1046 38.8954 14 40 14H48Z" fill="#4A5568" />
                <rect x="14" y="28" width="28" height="2" rx="1" fill="white" opacity="0.6" />
                <rect x="14" y="34" width="28" height="2" rx="1" fill="white" opacity="0.6" />
                <rect x="14" y="40" width="20" height="2" rx="1" fill="white" opacity="0.6" />
            </svg>
        );

        const handleFileChange = async (e) => {
            const files = Array.from(e.target.files || []);
            setError("");

            if (files.length === 0) return;

            // accept validation
            if (accept) {
                const acceptedTypes = accept.split(",").map(t => t.trim());
                for (const file of files) {
                    const ext = `.${file.name.split(".").pop().toLowerCase()}`;
                    const mime = file.type || "";

                    const ok = acceptedTypes.some(acceptType => {
                        if (!acceptType) return false;
                        if (acceptType.startsWith(".")) {
                            return ext === acceptType.toLowerCase();
                        } else if (acceptType.includes("*")) {
                            const base = acceptType.split("/")[0];
                            return mime.startsWith(base);
                        } else {
                            return mime === acceptType;
                        }
                    });

                    if (!ok) {
                        setError(`Invalid file type: ${file.name}. Accepted types: ${accept}`);
                        if (ref?.current) ref.current.value = "";
                        e.target.value = "";
                        return;
                    }
                }
            }

            const localCount = uploadedFiles.length;

            // maxFiles logic
            if (multiple) {
                if (localCount + files.length > maxFiles) {
                    setError(`Maximum ${maxFiles} file${maxFiles > 1 ? "s" : ""} allowed`);
                    if (ref?.current) ref.current.value = "";
                    e.target.value = "";
                    return;
                }
            } else {
                if (files.length > 1) {
                    setError(`Maximum ${maxFiles} file allowed`);
                    if (ref?.current) ref.current.value = "";
                    e.target.value = "";
                    return;
                }
            }

            // build preview objects
            const processed = await Promise.all(
                files.map(async (file) => {
                    const isImage = (file.type || "").startsWith("image/");
                    const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
                    let preview = null;

                    if (isImage) {
                        preview = await new Promise((resolve) => {
                            const reader = new FileReader();
                            reader.onload = (ev) => resolve(ev.target.result);
                            reader.readAsDataURL(file);
                        });
                    }

                    return {
                        name: file.name,
                        size: file.size,
                        type: file.type,
                        isImage,
                        isPdf,
                        preview,
                        file
                    };
                })
            );

            const newLocalFiles = multiple ? uploadedFiles.concat(processed) : processed;

            setUploadedFiles(newLocalFiles);

            // onChange
            if (onChange) {
                if (multiple) {
                    onChange({ target: { files: newLocalFiles.map(f => f.file).filter(Boolean) } });
                } else {
                    onChange({ target: { files: (processed[0] && processed[0].file) || null } });
                }
            }

            if (ref?.current) ref.current.value = "";
            e.target.value = "";
        };

        const removeFile = (index) => {
            const newFiles = uploadedFiles.filter((_, i) => i !== index);
            setUploadedFiles(newFiles);

            if (onChange) {
                if (multiple) {
                    onChange({ target: { files: newFiles.map(f => f.file).filter(Boolean) } });
                } else {
                    onChange({ target: { files: null } });
                }
            }

            if (ref?.current) ref.current.value = "";
        };

        const formatFileSize = (bytes) => {
            if (!bytes) return "";
            if (bytes === 0) return "0 Bytes";
            const k = 1024;
            const sizes = ["Bytes", "KB", "MB", "GB"];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i];
        };

        // add/select control:
        // multiple -> show while localCount < maxFiles
        // single   -> show if no local file OR uploadedImage exists (select under image)
        const localCount = uploadedFiles.length;
        const showAddControl = multiple
            ? localCount < maxFiles
            : (localCount < maxFiles || !!uploadedImage);

        return (
            <div className="font-urbanist">
                {(!!label || !isHideLabel) && (
                    <label
                        htmlFor={id}
                        className={`block text-lg font-semibold mb-1 ${disabled ? "text-gray-500" : "text-white"}`}
                    >
                        {label} {required && <span className="text-red-600">*</span>}
                    </label>
                )}

                {!!subTitle && (
                    <p className="text-xs text-gray-400 mb-1">
                        {subTitle}
                        {subRequired && <span className="text-red-600">*</span>}
                    </p>
                )}

                {/* No uploadedImage & no local file -> original dropzone */}
                {!uploadedImage && uploadedFiles.length === 0 ? (
                    <label
                        htmlFor={id}
                        className={`flex flex-col items-center justify-center border-2 border-dashed rounded-lg h-48 cursor-pointer hover:border-white transition-colors bg-[#22262E] text-center ${error ? "border-red-600" : "border-gray-600"
                            }`}
                    >
                        <img className="h-12 w-12" src="/assets/memberships/cloud-upload.png" alt="cloud-upload" />
                        <span className="text-gray-400 text-lg mt-2">Click to upload or drag and drop</span>
                        <span className="text-gray-500 text-sm font-semibold mt-0.5">
                            Upload {maxFiles} supported file{maxFiles > 1 ? "s" : ""}. Max {sizeInMb || 1} MB
                            {maxFiles > 1 ? " per file" : ""}
                        </span>
                        <input
                            ref={ref}
                            type="file"
                            id={id}
                            name={name}
                            multiple={multiple}
                            className="hidden"
                            required={required}
                            disabled={disabled}
                            accept={accept}
                            onChange={handleFileChange}
                        />
                    </label>
                ) : (
                    <div className="space-y-3">
                        {/* --------- uploadedImage preview (NO REMOVE BUTTON) --------- */}
                        {!uploadedFiles.length && uploadedImage && (
                            <div className="flex items-center gap-4 p-4 bg-[#22262E] border border-gray-600 rounded-lg">
                                {/* Assume image url; if not, you can swap logic */}
                                {/\.(jpg|jpeg|png|gif|webp|svg)$/i.test(uploadedImage) ? (
                                    <img
                                        src={uploadedImage}
                                        alt="uploaded"
                                        className="w-16 h-16 object-cover rounded"
                                    />
                                ) : (
                                    <PdfIcon />
                                )}

                                <div className="flex-1 min-w-0">
                                    <p className="text-white font-medium truncate">
                                        {uploadedImage.split("/").pop() || "Uploaded file"}
                                    </p>
                                    <p className="text-gray-400 text-sm">Previously uploaded</p>
                                </div>

                                {/* ❌ No remove button here */}
                            </div>
                        )}

                        {/* --------- local uploaded files (with remove) --------- */}
                        {uploadedFiles.map((file, index) => (
                            <div
                                key={index}
                                className="flex items-center gap-4 p-4 bg-[#22262E] border border-gray-600 rounded-lg"
                            >
                                {file.isImage ? (
                                    <img
                                        src={file.preview}
                                        alt={file.name}
                                        className="w-16 h-16 object-cover rounded"
                                    />
                                ) : file.isPdf ? (
                                    <PdfIcon />
                                ) : (
                                    <PdfIcon />
                                )}

                                <div className="flex-1 min-w-0">
                                    <p className="text-white font-medium truncate">{file.name}</p>
                                    <p className="text-gray-400 text-sm">{formatFileSize(file.size)}</p>
                                </div>

                                <button
                                    type="button"
                                    disabled={disabled}
                                    onClick={() => removeFile(index)}
                                    className="text-red-500 hover:text-red-400 transition-colors"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        ))}

                        {/* --------- Add/select control --------- */}
                        {showAddControl && (
                            <label
                                htmlFor={id}
                                className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-600 rounded-lg cursor-pointer hover:border-white transition-colors bg-[#22262E] text-gray-400"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                                <span>{multiple ? "Add another file" : "Select a file"}</span>
                                <input
                                    ref={ref}
                                    type="file"
                                    id={id}
                                    name={name}
                                    multiple={multiple}
                                    className="hidden"
                                    required={required}
                                    disabled={disabled}
                                    accept={accept}
                                    onChange={handleFileChange}
                                />
                            </label>
                        )}
                    </div>
                )}

                {(!!error || !!msg) && (
                    <p className="text-red-700 text-sm md:text-md mt-2">{error || msg}</p>
                )}
            </div>
        );
    }
);

export { FileUploader };
